import React from "react";
import { ListFilter } from "lucide-react";

import { FilterSingleSelect } from "@/components/common/FilterSingleSelect";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useRoomCategoriesFiltersSettings } from "@/hooks/useRoomCategories";
import { RoomFilters, RoomStatus } from "@/types/room";
import {
  ALL_CATEGORIES,
  ALL_STATUSES,
  countActiveRoomFilters,
  createRoomFilterDraft,
  getAppliedRoomFilterChanges,
  type RoomFilterDraft,
} from "../utils/roomFilterDraft";

const RoomCategoriesFilter = ({
  filters,
  actions,
  hotelId,
  showStatusFilter = true,
}: {
  hotelId: string;
  showStatusFilter?: boolean;
  filters: RoomFilters & { category?: string };
  actions: {
    setStatus: (value: string) => void;
    setCategory: (value: string) => void;
    handleFilterChange: (
      setter: (value: string) => void,
      value: string,
    ) => void;
  };
}) => {
  const { data: settings, isPending } =
    useRoomCategoriesFiltersSettings(hotelId);
  const [open, setOpen] = React.useState(false);
  const applied = createRoomFilterDraft(filters);
  const [draft, setDraft] = React.useState<RoomFilterDraft>(applied);

  if (isPending) {
    return null;
  }

  const activeCount = countActiveRoomFilters(applied);

  const handleApply = () => {
    const changes = getAppliedRoomFilterChanges(draft, applied);
    if (changes.status !== undefined) {
      actions.handleFilterChange(actions.setStatus, changes.status);
    }
    if (changes.category !== undefined) {
      actions.handleFilterChange(actions.setCategory, changes.category);
    }
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setDraft(createRoomFilterDraft(filters));
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="icon-text"
          className="text-foreground h-8 min-h-8 shrink-0 gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
        >
          <ListFilter className="size-4" />
          {activeCount > 0 ? `Filters (${activeCount})` : "Filters"}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="flex w-[320px] flex-col gap-4 rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        <h4 className="text-base leading-normal font-semibold text-[#171717]">
          Filters
        </h4>

        {showStatusFilter ? (
          <FilterSingleSelect
            value={draft.status}
            placeholder="All Status"
            className="w-full justify-between"
            options={[
              { value: ALL_STATUSES, label: "All Status" },
              { value: RoomStatus.AVAILABLE, label: "Free" },
              { value: RoomStatus.OUT_OF_SERVICE, label: "Unavailable" },
              { value: RoomStatus.OCCUPIED, label: "Occupied" },
            ]}
            onValueChange={(value: string) =>
              setDraft((previous) => ({ ...previous, status: value }))
            }
          />
        ) : null}

        <FilterSingleSelect
          value={draft.category}
          placeholder="Categories"
          className="w-full justify-between"
          options={[
            { value: ALL_CATEGORIES, label: "Categories" },
            ...(settings?.categories.map((category) => ({
              value: category.id,
              label: category.name,
            })) || []),
          ]}
          onValueChange={(value) =>
            setDraft((previous) => ({ ...previous, category: value }))
          }
        />

        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-8 rounded-md px-4 text-sm font-medium"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm font-medium text-[#FAFAFA] hover:bg-[#5F7597]"
            onClick={handleApply}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default RoomCategoriesFilter;
